import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getHuscleosClients, createHuscleosClient, updateHuscleosClient } from '../../lib/api';
import { ArrowLeft, Plus, CheckCircle2, Circle, ChevronDown, ChevronUp, Dumbbell, Heart, Flame, RefreshCw, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

const TIER_LABELS = { launch: 'Launch — $149/mo', grow: 'Grow — $299/mo', dominate: 'Dominate — $599/mo' };
const TEMPLATE_ICONS = { boutique: <Heart size={14} />, strength: <Dumbbell size={14} />, endurance: <Flame size={14} /> };
const STATUS_CONFIG = {
  onboarding: { color: '#F59E0B', bg: '#FFFBEB', border: '#FCD34D' },
  live:       { color: '#10B981', bg: '#ECFDF5', border: '#86EFAC' },
  paused:     { color: '#6B7280', bg: '#F9FAFB', border: '#E5E7EB' },
};

export default function ClientOnboarding() {
  const { user } = useAuth(); // eslint-disable-line no-unused-vars
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState({ gym_name: '', contact_name: '', email: '', phone: '', template: 'strength', tier: 'grow' });
  const [expanded, setExpanded] = useState(null);
  const [noteTexts, setNoteTexts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getHuscleosClients(); setClients(r.data.clients || []); }
    catch { toast.error('Failed to load clients'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newForm.gym_name || !newForm.contact_name) { toast.error('Gym name and contact required'); return; }
    try {
      await createHuscleosClient(newForm);
      toast.success(`Client created: ${newForm.gym_name}`);
      setNewForm({ gym_name: '', contact_name: '', email: '', phone: '', template: 'strength', tier: 'grow' });
      setCreating(false);
      load();
    } catch { toast.error('Failed to create client'); }
  };

  const toggleCheck = async (clientId, checklist, itemId) => {
    const updated = checklist.map(c => c.id === itemId ? { ...c, done: !c.done } : c);
    try {
      await updateHuscleosClient(clientId, { checklist: updated });
      setClients(prev => prev.map(c => c.id === clientId ? { ...c, checklist: updated } : c));
    } catch { toast.error('Failed to update'); }
  };

  const addNote = async (clientId) => {
    const text = (noteTexts[clientId] || '').trim();
    if (!text) return;
    try {
      await updateHuscleosClient(clientId, { note: text });
      setNoteTexts(prev => ({ ...prev, [clientId]: '' }));
      load();
      toast.success('Note added');
    } catch { toast.error('Failed'); }
  };

  const setStatus = async (clientId, status) => {
    try { await updateHuscleosClient(clientId, { status }); load(); toast.success(`Status: ${status}`); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="min-h-screen" style={{ background: '#FAFAFA', fontFamily: 'Manrope, sans-serif' }}>
      {/* Header */}
      <div className="border-b border-gray-200 sticky top-0 z-40 bg-white/90 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-4">
            <Link to="/staff/huscleos" className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"><ArrowLeft size={16} /> Pipeline</Link>
            <div className="w-px h-5 bg-gray-200" />
            <div className="flex items-center gap-1">
              <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: '#0F0F0F' }}>HUSCLE</span>
              <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: '#FF2E00' }}>OS</span>
              <span className="text-xs font-bold tracking-widest uppercase text-gray-500 ml-2">ONBOARDING</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="p-2 text-gray-500 hover:text-gray-900 transition-colors"><RefreshCw size={16} className={loading ? 'animate-spin' : ''} /></button>
            <button onClick={() => setCreating(true)} className="flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-4 py-2 bg-[#FF2E00] text-white hover:bg-[#E02900] transition-colors" data-testid="create-client-btn"><Plus size={14} /> New Client</button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* New client form */}
        {creating && (
          <div className="mb-6 p-6 bg-white border border-gray-200 shadow-sm" data-testid="new-client-form">
            <h3 className="text-sm font-bold tracking-widest uppercase text-gray-900 mb-4">New Client</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              {[{ k: 'gym_name', p: 'Gym Name *' }, { k: 'contact_name', p: 'Contact Name *' }, { k: 'email', p: 'Email' }, { k: 'phone', p: 'Phone' }].map(({ k, p }) => (
                <input key={k} placeholder={p} value={newForm[k]} onChange={e => setNewForm(f => ({ ...f, [k]: e.target.value }))}
                  className="p-3 text-sm text-gray-900 placeholder:text-gray-400 bg-gray-50 border border-gray-200 focus:border-[#FF2E00] focus:outline-none transition-colors" data-testid={`new-client-${k}`} />
              ))}
              <select value={newForm.template} onChange={e => setNewForm(f => ({ ...f, template: e.target.value }))} className="p-3 text-sm text-gray-900 bg-gray-50 border border-gray-200 focus:outline-none focus:border-[#FF2E00]" data-testid="new-client-template">
                <option value="boutique">Boutique</option><option value="strength">Strength</option><option value="endurance">Endurance</option>
              </select>
              <select value={newForm.tier} onChange={e => setNewForm(f => ({ ...f, tier: e.target.value }))} className="p-3 text-sm text-gray-900 bg-gray-50 border border-gray-200 focus:outline-none focus:border-[#FF2E00]" data-testid="new-client-tier">
                <option value="launch">Launch — $149/mo</option><option value="grow">Grow — $299/mo</option><option value="dominate">Dominate — $599/mo</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={handleCreate} className="text-xs font-bold tracking-widest uppercase px-5 py-2.5 bg-[#FF2E00] text-white hover:bg-[#E02900]" data-testid="save-client-btn">Create Client</button>
              <button onClick={() => setCreating(false)} className="text-xs font-bold tracking-widest uppercase px-5 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
            </div>
          </div>
        )}

        {loading && clients.length === 0 ? (
          <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-gray-400" /></div>
        ) : clients.length === 0 ? (
          <div className="text-center py-20"><p className="text-gray-500 mb-3">No clients yet.</p>
            <button onClick={() => setCreating(true)} className="text-sm font-bold text-[#FF2E00] hover:underline">Create your first client</button>
          </div>
        ) : (
          <div className="space-y-3">
            {clients.map(client => {
              const isOpen = expanded === client.id;
              const checklist = client.checklist || [];
              const done = checklist.filter(c => c.done).length;
              const total = checklist.length;
              const pct = total > 0 ? Math.round((done / total) * 100) : 0;
              const categories = [...new Set(checklist.map(c => c.category))];
              const sc = STATUS_CONFIG[client.status] || STATUS_CONFIG.paused;

              return (
                <div key={client.id} className="bg-white border border-gray-200 overflow-hidden" data-testid={`client-${client.id}`}>
                  {/* Client header */}
                  <div className="p-5 cursor-pointer flex items-center gap-4 hover:bg-gray-50 transition-colors" onClick={() => setExpanded(isOpen ? null : client.id)}>
                    <div className="shrink-0 text-gray-500">{TEMPLATE_ICONS[client.template] || <Dumbbell size={14} />}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-sm font-bold text-gray-900 truncate">{client.gym_name}</h3>
                        <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 border" style={{ color: sc.color, background: sc.bg, borderColor: sc.border }}>{client.status}</span>
                      </div>
                      <p className="text-xs text-gray-500">{client.contact_name} · {TIER_LABELS[client.tier] || client.tier}</p>
                    </div>
                    {/* Progress bar */}
                    <div className="hidden sm:flex items-center gap-3 shrink-0">
                      <div className="w-32 h-2 bg-gray-100 overflow-hidden"><div className="h-full transition-all" style={{ width: `${pct}%`, background: pct === 100 ? '#10B981' : '#FF2E00' }} /></div>
                      <span className="text-xs font-bold text-gray-500 w-10 tabular-nums">{pct}%</span>
                    </div>
                    {isOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                  </div>

                  {/* Expanded checklist */}
                  {isOpen && (
                    <div className="px-5 pb-5 border-t border-gray-100 pt-4 bg-[#FAFAFA]">
                      {/* Status controls */}
                      <div className="flex items-center flex-wrap gap-2 mb-5">
                        <span className="text-[10px] font-bold tracking-widest uppercase text-gray-500">Status:</span>
                        {['onboarding', 'live', 'paused'].map(s => {
                          const sc2 = STATUS_CONFIG[s];
                          const active = client.status === s;
                          return (
                            <button key={s} onClick={() => setStatus(client.id, s)}
                              className={`text-[10px] font-bold tracking-widest uppercase px-3 py-1 border transition-all ${active ? '' : 'opacity-60 hover:opacity-100'}`}
                              style={{ color: sc2.color, background: active ? sc2.bg : 'white', borderColor: active ? sc2.border : '#E5E7EB' }}>{s}</button>
                          );
                        })}
                      </div>

                      {/* Checklist by category */}
                      <div className="space-y-5">
                        {categories.map(cat => (
                          <div key={cat}>
                            <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-2">{cat}</p>
                            <div className="space-y-1">
                              {checklist.filter(c => c.category === cat).map(item => (
                                <button key={item.id} onClick={() => toggleCheck(client.id, checklist, item.id)}
                                  className="w-full flex items-center gap-3 p-2.5 hover:bg-white transition-colors text-left" data-testid={`check-${item.id}`}>
                                  {item.done ? <CheckCircle2 size={16} style={{ color: '#10B981' }} /> : <Circle size={16} className="text-gray-300" />}
                                  <span className={`text-sm ${item.done ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{item.task}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Notes */}
                      <div className="mt-5 pt-4 border-t border-gray-200">
                        <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-2">Notes</p>
                        <div className="flex gap-2 mb-3">
                          <input value={noteTexts[client.id] || ''} onChange={e => setNoteTexts(p => ({ ...p, [client.id]: e.target.value }))}
                            onKeyDown={e => e.key === 'Enter' && addNote(client.id)}
                            placeholder="Add a note..." className="flex-1 px-3 py-2 text-xs text-gray-900 placeholder:text-gray-400 bg-white border border-gray-200 focus:outline-none focus:border-[#FF2E00]" />
                          <button onClick={() => addNote(client.id)} className="px-3 py-2 bg-[#FF2E00] text-white hover:bg-[#E02900]"><Send size={12} /></button>
                        </div>
                        {(client.notes || []).slice().reverse().map((n, i) => (
                          <div key={i} className="flex items-start gap-2 p-2 bg-white border border-gray-100 mb-1">
                            <p className="text-xs text-gray-700 flex-1">{n.text}</p>
                            <span className="text-[10px] text-gray-400 shrink-0">{n.by} — {new Date(n.at).toLocaleDateString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
